import React, { useContext } from "react"
import styled from "styled-components"
import AvatarIcon from "~/components/twilio/AvatarIcon"
import { ParticipantsContext } from "./ParticipantsProvider"
import NetworkQualityLevel from "~/components/twilio/NetworkQualityLevel"
import AudioLevelIndicator from "~/components/twilio/AudioLevelIndicator"
import { CONNECTION_STATE } from "./ParticipantsProvider"
import { useCallVideoState } from "~/composable/useCallVideoStateZoom"
import type { Participant } from "@zoom/videosdk"

const MainParticipantInfoContainer = styled.div`
  & {
    height: 780px;
    position: relative;
    display: flex;
    align-items: center;

    @media screen and (max-width: 900px) {
      height: 600px;
    }

    @media screen and (max-width: 700px) {
      height: 250px;
    }

    .identity {
      background: rgba(0, 0, 0, 0.5);
      color: white;
      padding: 0.1em 0.3em 0.1em 0;
      display: inline-flex;

      & svg {
        margin-left: 0.1em;
      }

      margin-right: 0.4em;
      align-items: center;
    }

    .infoContainer {
      position: absolute;
      z-index: 2;
      height: 100%;
      width: 100%;
    }

    .reconnectingContainer {
      position: absolute;
      top: 0px;
      right: 0px;
      bottom: 0px;
      left: 0px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(40, 42, 43, 0.75);
      z-index: 1;
    }

    .avatarContainer {
      //height: 826px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: black;
      position: absolute;
      top: 0px;
      right: 0px;
      bottom: 0px;
      left: 0px;
      z-index: 1;

      & svg {
        transform: scale(2);
      }
    }

    .recordingIndicator {
      position: absolute;
      bottom: 10px;
      left: 20px;
      display: flex;
      align-items: center;
      color: white;
      padding: 0.1em 0.3em 0.1em 0;
      font-size: 1.2rem;
      height: 18px;
      @media screen and (max-width: 1185px) {
        display: none;
      }
    }

    .recordingCircle {
      height: 10px;
      width: 10px;
      background: red;
      border-radius: 100%;
      margin: 0 0.6em;
      animation: 1.25s pulsate ease-out infinite;
    }

    @keyframes pulsate {
      0% {
        background: #a90000;
      }

      50% {
        background: #f00;
      }

      100% {
        background: #a90000;
      }
    }
  }
`

const Typography = styled.p`
  font-size: 0.8rem;
`

const TypographyWarning = styled.p`
  font-size: 0.8rem;
  background-color: #f2131f;
  padding: 1px 12px;
  border-radius: 4px;
  margin-left: 8px;
`

const Tooltip = styled.p``

const VideoWrap = styled.div`
  height: 100%;
  width: 75%;
  margin: 0 auto;
  padding: 20px 0 40px 20px;
  position: relative;
  .info {
    z-index: 1;
    position: absolute;
    left: 30px;
    bottom: 95px;
    padding: 5px 10px;
    color: white;
    background: rgba(0, 0, 0, 0.8);
    span {
      margin-left: 5px;
    }
  }
  @media screen and (max-width: 1650px) {
    width: 100%;
  }
  @media screen and (max-width: 1185px) {
    padding: 10px;
    .info {
      bottom: 20px;
      left: 20px;
    }
  }
`

interface MainParticipantInfoProps {
  user: Participant
  children: React.ReactNode
}

export default function MainParticipantInfo({ user, children }: MainParticipantInfoProps) {
  const { connectionState, isRecording } = useContext(ParticipantsContext)
  const { participantList } = useCallVideoState()
  const userTypeName = participantList.find((item) => item.userId === user?.userId)?.userType || ""

  const isVideoSwitchedOff = !user?.bVideoOn

  return (
    <MainParticipantInfoContainer
      data-cy-main-participant
      data-cy-participant={user?.userId}
      className={"fullWidth"}
    >
      <div className={"infoContainer"}>
        <Tooltip
          title="All participants' audio and video is currently being recorded. Visit the app settings to stop recording."
          placement="top"
        >
          <div className={"recordingIndicator"}>
            <AudioLevelIndicator user={user} />
            <NetworkQualityLevel userId={user?.userId} />
            {isRecording ? (
              <>
                <div className={"recordingCircle"}></div>
                <Typography variant="body1" color="inherit" data-cy-recording-indicator>
                  録画中
                </Typography>
              </>
            ) : (
              <TypographyWarning>録画停止中</TypographyWarning>
            )}
          </div>
        </Tooltip>
      </div>

      {/* HungHV: shows the connection state of the current user - not the state of other participants in the room */}
      {CONNECTION_STATE.RECONNECTING === connectionState && (
        <div className={"reconnectingContainer"}>
          <Typography variant="body1" style={{ color: "white" }}>
            Reconnecting...
          </Typography>
        </div>
      )}

      {isVideoSwitchedOff && (
        <div className={"avatarContainer"}>
          <AvatarIcon />
        </div>
      )}

      <VideoWrap>
        <div className="info">
          <AudioLevelIndicator user={user} />
          <span>{userTypeName}</span>
        </div>
        {children}
      </VideoWrap>
    </MainParticipantInfoContainer>
  )
}
